// Gift card service.
// IMPORTANT: To avoid Firestore rule limitations on net-new collections,
// all gift-card data is stored in the existing "coupons" collection with
// a `kind` discriminator:
//   kind === "gift_template" → admin-defined denomination
//   kind === "gift_card"     → individual issued/purchased card
// validateCoupon in analytics.js skips entries with these kinds so the two
// flows do not interfere.
import {
  collection, doc, getDoc, getDocs, query, where, orderBy,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
  limit as fsLimit,
} from "firebase/firestore";
import { db } from "../firebase/config";

const COL = "coupons";

// ---------- templates ----------
export async function listGiftCardTemplates({ activeOnly = false } = {}) {
  try {
    const snap = await getDocs(query(collection(db, COL), where("kind", "==", "gift_template")));
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => (a.amount_azn || 0) - (b.amount_azn || 0));
    return activeOnly ? items.filter((i) => i.active !== false) : items;
  } catch { return []; }
}

export async function createGiftCardTemplate(data) {
  return await addDoc(collection(db, COL), {
    kind: "gift_template",
    name: (data.name || "").trim(),
    description: data.description || "",
    amount_azn: Number(data.amount_azn) || 0,
    image_url: data.image_url || "",
    multi_use: data.multi_use !== false,
    active: data.active !== false,
    created_at: serverTimestamp(),
  });
}

export async function updateGiftCardTemplate(id, patch) {
  const clean = { ...patch };
  if ("amount_azn" in clean) clean.amount_azn = Number(clean.amount_azn) || 0;
  await updateDoc(doc(db, COL, id), clean);
}

export async function deleteGiftCardTemplate(id) {
  await deleteDoc(doc(db, COL, id));
}

// ---------- issued cards ----------
function generateCode() {
  const ch = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const part = () => Array.from({ length: 4 }, () => ch[Math.floor(Math.random() * ch.length)]).join("");
  return `GC-${part()}-${part()}-${part()}`;
}

export async function issueGiftCard({
  template_id = null,
  name = "",
  amount_azn,
  multi_use = true,
  purchaser_user_id = null,
  purchaser_email = null,
  recipient_name = "",
  recipient_email = "",
  gift_message = "",
  order_id = null,
}) {
  let code = generateCode();
  for (let i = 0; i < 4; i++) {
    try {
      const dup = await getDocs(query(collection(db, COL), where("code", "==", code), fsLimit(1)));
      if (dup.empty) break;
    } catch { /* ignore */ }
    code = generateCode();
  }
  const initial = Number(amount_azn) || 0;
  const data = {
    kind: "gift_card",
    code,
    template_id,
    name,
    initial_amount_azn: initial,
    balance_azn: initial,
    multi_use: !!multi_use,
    status: "active",
    purchaser_user_id,
    purchaser_email,
    recipient_name,
    recipient_email,
    gift_message,
    order_id,
    // Coupon-shape mirror fields (so it lives happily alongside coupons):
    discount_amount: initial,
    discount_percent: 0,
    active: true,
    used: 0,
    max_uses: 1, // managed manually via status/balance instead
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COL), data);
  return { id: ref.id, ...data };
}

export async function listGiftCards() {
  try {
    const snap = await getDocs(query(collection(db, COL), where("kind", "==", "gift_card")));
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => {
      const at = a.created_at?.toDate ? a.created_at.toDate().getTime() : 0;
      const bt = b.created_at?.toDate ? b.created_at.toDate().getTime() : 0;
      return bt - at;
    });
    return items;
  } catch { return []; }
}

export async function listGiftCardsForUser(uid, email) {
  const all = await listGiftCards();
  const e = (email || "").toLowerCase();
  return all.filter((gc) =>
    (uid && gc.purchaser_user_id === uid) ||
    (e && gc.recipient_email && gc.recipient_email.toLowerCase() === e) ||
    (e && gc.purchaser_email && gc.purchaser_email.toLowerCase() === e)
  );
}

export async function listGiftCardsByOrder(orderId) {
  if (!orderId) return [];
  try {
    const snap = await getDocs(query(
      collection(db, COL),
      where("kind", "==", "gift_card"),
      where("order_id", "==", orderId)
    ));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    // fallback (e.g., composite index missing): full scan + client filter
    try {
      const all = await listGiftCards();
      return all.filter((g) => g.order_id === orderId);
    } catch { return []; }
  }
}

export async function validateGiftCardCode(code) {
  const norm = (code || "").toUpperCase().trim();
  if (!norm) return null;
  try {
    const snap = await getDocs(query(collection(db, COL), where("code", "==", norm), fsLimit(1)));
    if (snap.empty) return null;
    const c = { id: snap.docs[0].id, ...snap.docs[0].data() };
    if (c.kind !== "gift_card") return null;
    if (c.status !== "active") return null;
    if ((c.balance_azn || 0) <= 0) return null;
    return c;
  } catch { return null; }
}

export async function redeemGiftCard(id, amountUsed) {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const gc = snap.data();
  const used = Number(amountUsed) || 0;
  let patch;
  if (gc.multi_use) {
    const newBalance = Math.max(0, (gc.balance_azn || 0) - used);
    patch = {
      balance_azn: newBalance,
      status: newBalance > 0 ? "active" : "redeemed",
      last_used_at: serverTimestamp(),
    };
  } else {
    patch = {
      balance_azn: 0,
      status: "redeemed",
      last_used_at: serverTimestamp(),
    };
  }
  await updateDoc(ref, patch);
  return true;
}

export async function setGiftCardStatus(id, status) {
  await updateDoc(doc(db, COL, id), { status });
}

export async function deleteGiftCard(id) {
  await deleteDoc(doc(db, COL, id));
}
